import Big from "big.js"
import * as z from "zod"

import { createEntityFor } from "@axtesys/react-tools"

import { ItemArticle, ItemArticleEntity } from "./Article"
import { CompleteDiscount, Discount, DiscountEntity } from "./Discount"
import { TaxInfo } from "./TaxInfo"

export type InvoiceItemId = string

export type InvoiceItemBase = {
  amount: Big
  article: ItemArticle
  discounts: Discount[]
  isNegativeAmount: boolean
  isDiscountBlocked: boolean

  attachedToInvoiceItemId?: InvoiceItemId
  attachedArticle?: ItemArticle
  additionalText?: string
  isStandalone?: boolean
}

export type InvoiceItem = InvoiceItemBase & {
  invoiceItemId: InvoiceItemId
  createdAt: Date
  modifiedAt: Date
}

export type OutputInvoiceItem = Omit<InvoiceItem, "discounts"> & {
  taxInfo: TaxInfo
  discounts: CompleteDiscount[]

  discountedPricePerUnit?: Big
}

export const InvoiceItemEntity = createEntityFor<InvoiceItem>()
  .withSchema(
    z.object({
      invoiceItemId: z.string(),
      createdAt: z.string(),
      modifiedAt: z.string(),
      amount: z.string(),
      article: ItemArticleEntity.schema,
      discounts: z.array(DiscountEntity.schema),
      isNegativeAmount: z.boolean(),
      isDiscountBlocked: z.boolean(),
      attachedToInvoiceItemId: z.ostring(),
      attachedArticle: z.optional(ItemArticleEntity.schema),
      additionalText: z.ostring(),
      isStandalone: z.oboolean(),
    }),
  )
  .serialize(data => ({
    ...data,
    createdAt: data.createdAt.toISOString(),
    modifiedAt: data.modifiedAt.toISOString(),
    amount: data.amount.toString(),
    article: ItemArticleEntity.serialize(data.article),
    discounts: data.discounts.map(DiscountEntity.serialize),
    attachedArticle: data.attachedArticle
      ? ItemArticleEntity.serialize(data.attachedArticle)
      : undefined,
  }))
  .deserialize(json => ({
    ...json,
    createdAt: new Date(json.createdAt),
    modifiedAt: new Date(json.modifiedAt),
    amount: Big(json.amount),
    article: ItemArticleEntity.deserialize(json.article),
    discounts: json.discounts?.map(DiscountEntity.deserialize),
    attachedArticle: json.attachedArticle
      ? ItemArticleEntity.deserialize(json.attachedArticle)
      : undefined,
  }))
